import { useState } from "react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { useTranslation } from "react-i18next";
import { invoiceAPI } from "../services/api";
import type { InvoiceExportPayload, InvoiceExportRow } from "../types/invoice";

/**
 * The invoice listing, as a spreadsheet the accounting system imports.
 *
 * One sheet, one row per billed service, with its columns in the order the
 * accounting system's sales-invoice import reads them — including the columns
 * we have nothing to put in, since the import reads columns by position and a
 * missing one shifts everything after it.
 *
 * The join and the accounting lookup happen server-side (GET /invoices/export)
 * so the browser only lays rows out; the workbook is built here, like the
 * shipment export, because column order, number formats and translated headers
 * are presentation and belong with the rest of the UI.
 */

/** Filters are the listing's — the export is "this screen, as a spreadsheet". */
export interface InvoiceExportFilters {
  status?: string;
  invoiceType?: string;
  clientId?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * `key` names the header (invoicing:exportColumns.<key>). `value` absent means
 * the import has the column and we have nothing for it: it is still written,
 * empty, so the columns after it stay where they are.
 */
type Col = {
  key: string;
  widthCh: number;
  numFmt?: string;
  value?: (row: InvoiceExportRow) => unknown;
};

const MONEY = "#,##0.00";
const DATE = "dd.mm.yyyy";
// Codes as text, not General: a code such as "01" or an account such as
// 0411001 loses its leading zero the moment Excel decides it is a number.
const TEXT = "@";

/** Dates go in as Dates so Excel can sort and filter them as dates. */
const toDate = (value: string | null | undefined) =>
  value ? new Date(value) : null;

const NOT_FOR_LEDGER = new Set(["draft", "cancelled"]);

const confirmedFlag = (status: InvoiceExportRow["status"]) =>
  !status || NOT_FOR_LEDGER.has(status) ? null : 0;

const oneLine = (value: string | null | undefined) =>
  (value || "").replace(/\s*[\r\n]+\s*/g, " ").trim();

/** The import's columns, in its order. */
const COLUMNS: Col[] = [
  { key: "invoiceNumber", widthCh: 13, value: (r) => r.invoiceNumber },
  {
    key: "invoiceDate",
    widthCh: 13,
    numFmt: DATE,
    value: (r) => toDate(r.invoiceDate),
  },
  { key: "paymentTerms", widthCh: 21, value: (r) => r.paymentTerms },
  {
    key: "dueDate",
    widthCh: 12,
    numFmt: DATE,
    value: (r) => toDate(r.dueDate),
  },
  { key: "lateInterest", widthCh: 13 },
  { key: "clientCode", widthCh: 13, numFmt: TEXT, value: (r) => r.clientCode },
  { key: "clientContact", widthCh: 15 },
  { key: "clientEmail", widthCh: 26, value: (r) => r.clientEmail },
  { key: "seller", widthCh: 18, value: (r) => r.seller },
  { key: "language", widthCh: 10 },
  // The truck the container was dispatched on.
  { key: "truck", widthCh: 14, value: (r) => r.truck },
  { key: "receivablesAccount", widthCh: 20 },
  { key: "warehouse", widthCh: 11 },
  {
    key: "serviceCode",
    widthCh: 16,
    numFmt: TEXT,
    value: (r) => r.serviceCode,
  },
  { key: "vatCode", widthCh: 12, numFmt: TEXT, value: (r) => r.vatCode },
  { key: "unit", widthCh: 9 },
  {
    key: "quantity",
    widthCh: 10,
    numFmt: "#,##0.###",
    value: (r) => r.quantity,
  },
  { key: "discount", widthCh: 11 },
  { key: "unitPrice", widthCh: 12, numFmt: MONEY, value: (r) => r.unitPrice },
  { key: "purchasePrice", widthCh: 15 },
  {
    key: "incomeAccount",
    widthCh: 16,
    numFmt: TEXT,
    value: (r) => r.incomeAccount,
  },
  { key: "confirmed", widthCh: 11, value: (r) => confirmedFlag(r.status) },
  { key: "description", widthCh: 40, value: (r) => oneLine(r.description) },
  { key: "lineObject", widthCh: 12 },
];

export const useInvoiceExport = (filters: InvoiceExportFilters) => {
  const { t } = useTranslation(["invoicing", "common"]);
  const [exporting, setExporting] = useState(false);

  const buildSheet = (workbook: ExcelJS.Workbook, rows: InvoiceExportRow[]) => {
    const sheet = workbook.addWorksheet(t("invoicing:list.exportSheetLines"), {
      // The header stays put while a few thousand rows scroll under it.
      views: [{ state: "frozen", ySplit: 1 }],
    });

    sheet.columns = COLUMNS.map((col) => ({
      header: t(`invoicing:exportColumns.${col.key}`),
      width: col.widthCh,
    }));

    const header = sheet.getRow(1);
    header.font = { bold: true };
    header.alignment = { vertical: "middle" };
    header.height = 22;

    for (const row of rows) {
      sheet.addRow(
        COLUMNS.map((col) => {
          // No column is mandatory: whatever is missing is left blank — and
          // blank, not "": an empty-string cell is still a value to an
          // importer, and a blank code is "none", not "the empty code".
          const value = col.value?.(row);
          return value === "" || value === undefined ? null : value;
        }),
      );
    }

    COLUMNS.forEach((col, i) => {
      if (col.numFmt) sheet.getColumn(i + 1).numFmt = col.numFmt;
    });

    // Filter dropdowns over the populated range — the first thing anyone does
    // with a sheet like this is narrow it to one client or one account.
    if (rows.length) {
      sheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: rows.length + 1, column: COLUMNS.length },
      };
    }

    return sheet;
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // No page or limit: the endpoint returns everything the filters match,
      // or refuses with a count when that is too much to put in one file.
      const response = await invoiceAPI.exportRows({
        status: filters.status || undefined,
        invoiceType: filters.invoiceType || undefined,
        clientId: filters.clientId || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      });
      const payload = response.data.data as InvoiceExportPayload;

      if (!payload.rows.length) {
        toast.info(t("invoicing:list.exportEmpty"));
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.created = new Date();

      buildSheet(workbook, payload.rows);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoices-${new Date().toISOString().split("T")[0]}.xlsx`;
      link.click();
      // Released once the download has been handed off — without this the
      // blob is held for the life of the tab, and this one is megabytes.
      URL.revokeObjectURL(url);

      toast.success(
        t("invoicing:list.exportDone", {
          invoices: payload.meta.invoiceCount,
          lines: payload.meta.lineCount,
        }),
      );
    } catch (error) {
      console.error("Invoice export error:", error);
      // The endpoint's own message says what to do about it (narrow the dates,
      // filter by client) — far better than a generic failure.
      const message = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(message || t("invoicing:list.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  return { handleExport, exporting };
};
